import ExcelJS from "exceljs";

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

function cellDisplayValue(value) {
  if (value == null) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const d = String(value.getDate()).padStart(2, "0");
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const y = value.getFullYear();
    return `${d}/${m}/${y}`;
  }
  if (typeof value === "object") {
    if (value.text != null) return String(value.text);
    if (value.result != null) return cellDisplayValue(value.result);
    if (value.richText) {
      return value.richText.map((t) => t.text || "").join("");
    }
  }
  return value;
}

/**
 * Export the uploaded workbook shape + a last column "Validation Status"
 * for every data row (Success or full fail reason).
 *
 * @param {File|Blob|ArrayBuffer} sourceFile - original upload
 * @param {Array<{row:number, status:string, reason:string}>} reviewedRows
 * @param {string} filename
 * @param {object} [opts]
 * @param {string} [opts.sheetName] - prefer this sheet; else first data sheet
 */
export async function downloadValidationStatusWorkbook(
  sourceFile,
  reviewedRows = [],
  filename = "Import_Validation_Status.xlsx",
  opts = {}
) {
  if (!sourceFile) {
    throw new Error("Original upload file is required for the validation report");
  }

  const statusByRow = new Map();
  for (const r of reviewedRows || []) {
    const rowNum = Number(r.row);
    if (!Number.isFinite(rowNum)) continue;
    const label =
      r.status === "success" || String(r.reason || "").toLowerCase() === "success"
        ? "Success"
        : r.reason || "Validation failed";
    statusByRow.set(rowNum, label);
  }

  const buffer =
    sourceFile instanceof ArrayBuffer
      ? sourceFile
      : await sourceFile.arrayBuffer();

  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buffer);

  const preferred = opts.sheetName
    ? wb.getWorksheet(opts.sheetName)
    : null;
  const ws =
    preferred ||
    wb.worksheets.find((s) => /import\s*data/i.test(s.name)) ||
    wb.worksheets.find((s) => !/instruction/i.test(s.name)) ||
    wb.worksheets[0];

  if (!ws) throw new Error("No worksheet found in the uploaded file");

  const headerRow = ws.getRow(1);
  let lastCol = 0;
  headerRow.eachCell({ includeEmpty: false }, (_c, col) => {
    if (col > lastCol) lastCol = col;
  });
  if (lastCol < 1) lastCol = ws.columnCount || 1;

  const statusCol = lastCol + 1;
  headerRow.getCell(statusCol).value = "Validation Status";
  headerRow.getCell(statusCol).font = { bold: true };

  const maxRow = Math.max(ws.rowCount || 1, ...statusByRow.keys(), 1);
  for (let r = 2; r <= maxRow; r += 1) {
    const row = ws.getRow(r);
    let hasData = false;
    for (let c = 1; c <= lastCol; c += 1) {
      const v = cellDisplayValue(row.getCell(c).value);
      if (v !== "" && v != null) {
        hasData = true;
        break;
      }
    }
    if (!hasData && !statusByRow.has(r)) continue;
    row.getCell(statusCol).value = statusByRow.get(r) || "Success";
  }

  const out = await wb.xlsx.writeBuffer();
  triggerBlobDownload(
    new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    filename
  );
}

/**
 * When the original file is unavailable, build a sheet from reviewed row.raw
 * objects + Validation Status.
 *
 * @param {Array} reviewedRows
 * @param {string} filename
 * @param {{ columns?: Array<{ key: string, header: string, width?: number }> }} [opts]
 */
export async function downloadValidationStatusFromRows(
  reviewedRows = [],
  filename = "Import_Validation_Status.xlsx",
  opts = {}
) {
  const rows = Array.isArray(reviewedRows) ? reviewedRows : [];
  if (!rows.length) throw new Error("No rows to export");

  const defaultChecksColumns = [
    { key: "row", header: "Excel Row", width: 10 },
    { key: "employeeCode", header: "Employee Code", width: 14 },
    { key: "employeeName", header: "Employee Name", width: 22 },
    { key: "attendanceDate", header: "Attendance Date", width: 14 },
    { key: "punchTime", header: "Punch Time", width: 12 },
    { key: "punchKind", header: "Punch Type", width: 10 },
    { key: "deviceId", header: "Device ID", width: 12 },
    { key: "remarks", header: "Remarks", width: 20 },
    { key: "validationStatus", header: "Validation Status", width: 14 },
    { key: "reason", header: "Details / Reason", width: 48 },
  ];

  let columns = Array.isArray(opts.columns) && opts.columns.length ? opts.columns : null;
  if (!columns) {
    const sample = rows[0]?.raw || rows[0]?.record || {};
    const looksLikeChecks =
      sample.attendanceDate != null ||
      sample.punchKind != null ||
      sample.punchTime != null ||
      sample.punchDateTime != null;
    if (looksLikeChecks) columns = defaultChecksColumns;
  }

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Validation");

  if (columns) {
    ws.columns = columns.map((c) => ({
      header: c.header,
      key: c.key,
      width: c.width || 14,
    }));
    ws.getRow(1).font = { bold: true };
    ws.getRow(1).alignment = { vertical: "middle", wrapText: true };
    ws.views = [{ state: "frozen", ySplit: 1 }];

    const sorted = [...rows].sort((a, b) => Number(a.row) - Number(b.row));
    for (const r of sorted) {
      const raw = r.raw || r.record || {};
      const statusLabel =
        r.status === "success" ? "Success" : "Failed";
      const detail =
        r.status === "success"
          ? r.reason && r.reason !== "Success"
            ? r.reason
            : raw.reason && raw.reason !== "OK"
              ? raw.reason
              : "OK"
          : r.reason || raw.reason || "Validation failed";
      const line = {};
      for (const c of columns) {
        if (c.key === "validationStatus") {
          line[c.key] = statusLabel;
          continue;
        }
        if (c.key === "reason") {
          line[c.key] = detail;
          continue;
        }
        if (c.key === "row") {
          line[c.key] = r.row ?? raw.row ?? "";
          continue;
        }
        if (c.key === "employeeCode") {
          line[c.key] = r.code || raw.employeeCode || raw.empCode || "";
          continue;
        }
        if (c.key === "employeeName") {
          line[c.key] = r.name || raw.employeeName || raw.name || "";
          continue;
        }
        if (c.key === "punchTime") {
          line[c.key] =
            raw.punchTime ||
            raw.punchDateTime ||
            raw.punchedAt ||
            "";
          continue;
        }
        if (c.key === "punchKind") {
          line[c.key] = raw.punchKind || raw.punchType || "";
          continue;
        }
        line[c.key] = cellDisplayValue(raw[c.key]);
      }
      const excelRow = ws.addRow(line);
      if (statusLabel === "Failed") {
        excelRow.getCell("validationStatus").font = {
          color: { argb: "FFB91C1C" },
          bold: true,
        };
      } else {
        excelRow.getCell("validationStatus").font = {
          color: { argb: "FF15803D" },
          bold: true,
        };
      }
    }
  } else {
    const headerSet = [];
    const seen = new Set();
    const skipKeys = new Set([
      "_commit",
      "fieldErrors",
      "warnings",
      "record",
      "raw",
      "__row",
      "iso",
      "ms",
      "employeeId",
    ]);
    for (const r of rows) {
      const raw = r.raw || r.record || {};
      Object.keys(raw).forEach((k) => {
        if (skipKeys.has(k) || seen.has(k)) return;
        seen.add(k);
        headerSet.push(k);
      });
    }
    const preferred = [
      "row",
      "employeeCode",
      "employeeName",
      "attendanceDate",
      "punchTime",
      "punchDateTime",
      "punchKind",
      "punchType",
      "deviceId",
      "remarks",
      "reason",
    ];
    const ordered = [
      ...preferred.filter((k) => seen.has(k)),
      ...headerSet.filter((k) => !preferred.includes(k)),
      "Validation Status",
    ];

    ws.addRow(ordered);
    ws.getRow(1).font = { bold: true };
    ws.views = [{ state: "frozen", ySplit: 1 }];

    const sorted = [...rows].sort((a, b) => Number(a.row) - Number(b.row));
    for (const r of sorted) {
      const raw = r.raw || r.record || {};
      const line = ordered.map((h) => {
        if (h === "Validation Status") {
          return r.status === "success" ? "Success" : r.reason || "Validation failed";
        }
        if (h === "row") return r.row ?? raw.row ?? "";
        if (h === "employeeCode") return r.code || raw.employeeCode || "";
        if (h === "employeeName") return r.name || raw.employeeName || "";
        return cellDisplayValue(raw[h]);
      });
      ws.addRow(line);
    }

    ordered.forEach((h, i) => {
      ws.getColumn(i + 1).width = Math.min(48, Math.max(12, String(h).length + 2));
    });
  }

  const out = await wb.xlsx.writeBuffer();
  triggerBlobDownload(
    new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    filename
  );
}
